/**
 * SubBlockCard — one sub-block (תת-בלוק) inside a block of the menu builder.
 *
 * Deliberately NOT a card: depth is conveyed by an indent + a mint start rail
 * + a hover tint, so the block card stays the only elevated surface.
 * Footer is the mint totals strip — tier 2 of the live nutrition.
 *
 * Like the block, its name is optional: unnamed → no heading on the client
 * page, and an inline hint here says so.
 */

import { BookmarkSimple, CaretDown, CaretUp, Hamburger, Info, Trash } from "@phosphor-icons/react";

import { calcSubBlockNutrition } from "../../../utils/nutritionCalc";
import HoverTip from "../../ui/HoverTip";
import IngredientRow from "./IngredientRow";
import MacroSummaryStrip from "./MacroSummaryStrip";
import NmbIconButton from "./NmbIconButton";
import "./MenuCards.css";

const EMPTY_NAME_TIP = "בלי שם, הפריטים יוצגו ללקוח/ה ישירות, בלי כותרת משנה";

const SubBlockCard = ({ subBlock, blockIndex, index, count, ctx }) => {
  const cardKey = `sub:${subBlock.subBlockId}`;
  const error = ctx.errors[cardKey];
  const isFlashing = ctx.flashKey === cardKey;
  const ingredients = subBlock.ingredients || [];
  const totals = calcSubBlockNutrition(subBlock);
  const isUnnamed = !(subBlock.subBlockName || "").trim();

  const dropZone = (position) =>
    ctx.canDrop("ingredient") ? (
      <button
        type="button"
        className="nmb-dropzone ingredient"
        onClick={() =>
          ctx.onDropHere({
            level: "ingredient",
            blockIndex,
            subBlockIndex: index,
            index: position,
          })
        }
      >
        הוסף כאן
      </button>
    ) : null;

  return (
    <div
      className={`nmb-sub ${isFlashing ? "flash" : ""} ${error ? "invalid" : ""}`}
      data-nmb-key={cardKey}
    >
      <div className="nmb-sub-head">
        <HoverTip text={isUnnamed ? EMPTY_NAME_TIP : ""} className="nmb-name-tip" placement="bottom">
          <input
            type="text"
            className={`nmb-sub-name ${isUnnamed ? "is-empty" : ""}`}
            value={subBlock.subBlockName || ""}
            onChange={(event) =>
              ctx.onSubBlockChange(blockIndex, index, { subBlockName: event.target.value })
            }
            placeholder={`שם התת-בלוק ${index + 1} (לא חובה)`}
            aria-label={`שם התת-בלוק ${index + 1}`}
          />
        </HoverTip>

        <div className="nmb-sub-actions">
          <NmbIconButton
            label="העלאת התת-בלוק למעלה"
            disabled={index === 0}
            onClick={() => ctx.onMoveSubBlock(blockIndex, index, -1)}
          >
            <CaretUp size={14} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="הורדת התת-בלוק למטה"
            disabled={index === count - 1}
            onClick={() => ctx.onMoveSubBlock(blockIndex, index, 1)}
          >
            <CaretDown size={14} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="שמירת התת-בלוק כתבנית"
            variant="alt"
            onClick={() => ctx.onSaveSubBlockTemplate(blockIndex, index)}
          >
            <BookmarkSimple size={14} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="הוספת מזון"
            variant="mint"
            onClick={() => ctx.onAddFood(blockIndex, index)}
          >
            <Hamburger size={14} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="מחיקת התת-בלוק"
            variant="danger"
            onClick={() => ctx.onDeleteSubBlock(blockIndex, index)}
          >
            <Trash size={14} weight="fill" />
          </NmbIconButton>
        </div>
      </div>

      {isUnnamed && ingredients.length > 0 && (
        <p className="nmb-name-hint">
          <Info size={12} weight="fill" />
          בלי שם, הפריטים יוצגו ללקוח/ה ישירות, ללא כותרת משנה.
        </p>
      )}

      {error && <p className="nmb-card-error">{error}</p>}

      <div className="nmb-sub-body">
        {dropZone(0)}

        {ingredients.length === 0 && !ctx.canDrop("ingredient") ? (
          <p className="nmb-inline-empty">
            עדיין לא נוספו מזונות לתת-בלוק הזה. אפשר להוסיף מזון בעזרת הכפתור למעלה.
          </p>
        ) : null}

        {ingredients.map((ingredient, ingredientIndex) => (
          <div key={ingredient.ingredientId || `ing-${ingredientIndex}`}>
            <IngredientRow
              ingredient={ingredient}
              blockIndex={blockIndex}
              subBlockIndex={index}
              index={ingredientIndex}
              count={ingredients.length}
              ctx={ctx}
            />
            {dropZone(ingredientIndex + 1)}
          </div>
        ))}
      </div>

      <button
        type="button"
        className="nmb-add-btn ghost"
        onClick={() => ctx.onAddFood(blockIndex, index)}
      >
        <Hamburger size={13} weight="fill" />
        <span>הוסף מזון</span>
      </button>

      {/* Tier 2 of the live nutrition */}
      {ingredients.length > 0 && (
        <MacroSummaryStrip
          totals={totals}
          label="סיכום ערכים לתת-בלוק"
          tip="סכום הערכים של המזונות בתת-בלוק הזה, לפי הכמות ויחידת המידה של כל פריט"
          variant="mint"
        />
      )}
    </div>
  );
};

export default SubBlockCard;
